import { useContext, useEffect, useMemo } from "react"
import FlotiMapContext from "./context/context"
import { useTranslation } from "react-i18next"
import ButtonSelect from "../Input/ButtonSelect"
import Icon from "../Icon"
import { ICON } from "../Icon/data"
import { MAP_TYPE_IDS, MAP_TYPES, MAP_VIEW_OPTIONS } from "./data"
import { setInitialMapOptions, setMapTile, setViewOptions } from "./context/actions"
import Input from "../Input"
import { MapOptions } from "./type"
import { editUserPreferences } from "../../services/calls/users"
import UserContext from "../../contexts/UserContext/context"
import { PreferencePatchKey } from "../../contexts/UserContext/types"
import { updateUserPreferences } from "../../contexts/UserContext/actions"

export const useOptions = (options: MapOptions) => {
  const { t } = useTranslation()
  const { user, dispatch: userDispatch } = useContext(UserContext)
  const { flotiMap, dispatch } = useContext(FlotiMapContext)
  const { viewOptions } = flotiMap

  useEffect(() => {
    const enableClusters = user.preferences?.ENABLE_CLUSTER ?? false
    const tile = user.preferences?.BASELAYER ?? MAP_TYPE_IDS.OPEN_STREETMAP
    dispatch(setInitialMapOptions({
      tile,
      viewOptions: {
        clusterMarkers: options.showClusterMarkers && enableClusters,
      }
    }))
  }, [])

  const handleTileChange = (id: MAP_TYPE_IDS) => {
    dispatch(setMapTile(id))
    userDispatch(updateUserPreferences({ BASELAYER: id }))
    editUserPreferences({ [PreferencePatchKey.BASELAYER]: id })
  }

  const handleViewOptionChange = (key: string, value: boolean) => {
    dispatch(setViewOptions({ [key]: value }))
    if (key === 'clusterMarkers') {
      userDispatch(updateUserPreferences({ ENABLE_CLUSTER: value }))
      editUserPreferences({ [PreferencePatchKey.ENABLE_CLUSTER]: value })
    }
  }

  const mapTypeSelector = useMemo(() => {
    return (
      <ButtonSelect
        label={(
          <>
            {t('Map type')}
            <Icon icon={ICON.GLOBE} />
          </>
        )}
        options={
          Object.keys(MAP_TYPES).reduce((obj, key) => {
            obj[key] = MAP_TYPES[key].label
            return obj
          }, {})
        }
        onChange={(id) => handleTileChange(id)}
        dropdownAlign="left"
        dropdownWidth={160}
      />
    )
  }, [dispatch, t])

  const viewOptionsSelector = useMemo(() => {
    return (
      <ButtonSelect
        closeOnSelection={false}
        dropdownWidth={210}
        label={(
          <>
            {t('View options')}
            <Icon icon={ICON.COG_VIEW} />
          </>
        )}
        options={
          Object.keys(MAP_VIEW_OPTIONS).reduce((obj, key) => {
            const { label, icon, isVisible } = MAP_VIEW_OPTIONS[key]
            if (isVisible(options)) {
              const value = viewOptions[key]
              obj[key] = (
                <div className="FlotiMap__options">
                  <div className="FlotiMap__options-label">
                    <Icon icon={icon} />
                    {t(label)}
                  </div>
                  <Input
                    type="toggle"
                    size="s"
                    value={value}
                    onInput={(val) => handleViewOptionChange(key, val)}
                  />
                </div>
              )
            }
            return obj
          }, {})
        }
        onChange={(key) => handleViewOptionChange(key as string, !viewOptions[key])}
        dropdownAlign="left"
      />
    )
  }, [dispatch, viewOptions, t, options])

  return {
    mapTypeSelector,
    viewOptionsSelector
  }
}
